import { LinkOutlined } from "@ant-design/icons";
import { Card } from "antd";
import ErrorBoundary from "components/error-boundary";
import {
  EWorkflowStatusDataBlock,
  ProjectPermissionEnum
} from "constants/index";
import {
  isPermissionNotGrantted,
  ProjectContext
} from "context/ProjectProvider";
import { FeatureTypes } from "entity-app/constants";
import {
  TFeatureWorkflowInstance,
  TFetureDetailUpdateAction
} from "entity-app/models";
import EntityLeadTimeView from "entity-app/shared-components/entity-dateblock/components/entity-lead-time/entity-lead-time-view";
import EntityRiskStatementView from "entity-app/shared-components/entity-dateblock/components/entity-risk-statement/entity-risk-statement-view";
import DateblockSubmittalMaterialEntity from "entity-app/shared-components/entity-dateblock/submittal-material-dateblock";
import { useContext } from "react";
import { Link, useParams } from "react-router-dom";

function HeadingDateBlock(props: {
  featureInstanceData: any;
  actions: TFetureDetailUpdateAction;
  isRevisionMode: boolean;
  disableDB?: boolean;
}) {
  const { projectId } = useParams() as any;
  const {
    tokenContents,
    columnHeaders: { materialHeaderMap }
  } = useContext(ProjectContext);
  const { featureInstanceData, actions, isRevisionMode, disableDB } = props;
  if (!featureInstanceData) return null;
  const { feature_workflow_instance: featureWorkflowInstance } =
    featureInstanceData as {
      feature_workflow_instance: TFeatureWorkflowInstance;
    };

  const canOpenLinkingPage = !isPermissionNotGrantted(
    ProjectPermissionEnum.ViewLinkingPage,
    tokenContents?.role!
  );

  const title =
    featureInstanceData.feature_type_id === FeatureTypes.SUBMITTALS
      ? "Submittal Schedule"
      : "Material Schedule";
  const to =
    featureInstanceData.feature_type_id === FeatureTypes.SUBMITTALS
      ? `/project/${projectId}/schedule/submittal-linking?&SubmittalId=${featureInstanceData.id}`
      : `/project/${projectId}/schedule/material-linking?&materialId=${featureInstanceData.id}`;

  return (
    <div className="flex w-full items-center" data-testid="slts-heading-root">
      <div className="flex items-center space-x-4">
        <div data-testid="slts-title-section">
          {title}
          {!disableDB && canOpenLinkingPage && (
            <Link
              to={to}
              title="Add Linking"
              data-testid="slts-link-add-linking"
            >
              <LinkOutlined
                style={{
                  color: "var(--ciq-blue-500)",
                  fontSize: 16,
                  marginLeft: 10,
                  marginTop: 2
                }}
                data-testid="slts-icon-link"
              />
            </Link>
          )}
        </div>
        {isRevisionMode ? (
          <span
            className="submittal--warning-message"
            data-testid="slts-warning-message"
          >
            To view the material schedule of the linked materials, please
            navigate to the latest revision.
          </span>
        ) : (
          ""
        )}
        {featureInstanceData.feature_type_id === FeatureTypes.MATERIALS && (
          <div className="pl-3" data-testid="slts-lead-time-container">
            <EntityLeadTimeView
              featureWorkflowInstance={featureInstanceData}
              materialHeaderMap={materialHeaderMap}
              data-testid="slts-lead-time-view"
            />
          </div>
        )}
        {!disableDB && (
          <div
            className="pl-3 text-ciq-gray-800 text-opacity-80"
            data-testid="slts-risk-statement-container"
          >
            <EntityRiskStatementView
              actions={actions}
              featureWorkflowInstance={featureWorkflowInstance}
              data-testid="slts-risk-statement-view"
            />
          </div>
        )}
      </div>
    </div>
  );
}

function SubmittalLinkedTopSection(props: {
  featureInstanceData: any;
  actions: TFetureDetailUpdateAction;
  isRevisionMode?: boolean;
  disableDB?: boolean;
}) {
  const { featureInstanceData, actions, isRevisionMode, disableDB } = props;

  const isVoidEntity =
    featureInstanceData?.feature_workflow_instance?.workflow_status ===
    EWorkflowStatusDataBlock.VOID;
  const isDeletedEntity = featureInstanceData?.deleted_from_source;

  return (
    <Card
      title={
        <HeadingDateBlock
          featureInstanceData={featureInstanceData}
          actions={actions}
          isRevisionMode={isRevisionMode || false}
          disableDB={disableDB || isVoidEntity || isDeletedEntity}
        />
      }
      bodyStyle={{ padding: 0 }}
      className="ciq-card-block"
      data-testid="slts-card"
      extra={
        <div
          className="flex space-x-2 justify-end pb-2 h-10 items-center"
          data-testid="slts-extra"
        >
          <div className="pr-6" data-testid="slts-governing-date-label">
            * Governing date
          </div>
        </div>
      }
    >
      <div
        className="w-full h-[470px] flex"
        data-testid="slts-content-container"
      >
        <div
          className="flex overflow-auto h-full w-full items-center"
          data-testid="slts-scroll-container"
        >
          <ErrorBoundary>
            <DateblockSubmittalMaterialEntity
              featureInstanceData={featureInstanceData}
              afterChange={() => {
                actions.refetchFeatureInstanceData();
              }}
              disableDB={
                disableDB || isVoidEntity || isDeletedEntity || isRevisionMode
              }
              data-testid="slts-dateblock-entity"
            />
          </ErrorBoundary>
        </div>
      </div>
    </Card>
  );
}
export default SubmittalLinkedTopSection;
